import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import type { RowData } from '@tanstack/react-table';
import { useEffect, useEffectEvent, useState } from 'react';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { cn } from '@/lib/utils';
import type { ListSearch } from '@/lib/url-state';
import type { DataTableController } from './use-data-table';

const DEBOUNCE_MS = 300;

interface DraftState {
  text: string;
  /** The URL value the text was last synced with. */
  basis: string;
  /** A value sent to the URL that hasn't arrived yet. */
  pending: string | null;
}

/**
 * Quick search, written to `?q=` as the user types (debounced, history-replacing). The URL
 * stays the source of truth: Back, saved views and "Clear filters" update the field.
 */
export function DataTableSearch<TData extends RowData, TSearch extends ListSearch>({
  controller,
  placeholder,
  className,
}: {
  controller: DataTableController<TData, TSearch>;
  placeholder: string;
  className?: string;
}) {
  const committed = controller.search.q ?? '';
  const [state, setState] = useState<DraftState>({ text: committed, basis: committed, pending: null });

  // Reconcile with the URL during render (React's "adjust state on prop change" pattern).
  let current = state;
  if (state.pending !== null && committed === state.pending) {
    current = { ...state, basis: committed, pending: null };
  } else if (state.pending === null && committed !== state.basis) {
    current = { text: committed, basis: committed, pending: null };
  }
  if (current !== state) setState(current);

  const commitNow = (next: string) => {
    setState((s) => ({ ...s, pending: next }));
    controller.setSearch({ q: next || undefined } as Partial<TSearch>, { replace: true });
  };
  const commit = useEffectEvent(commitNow);

  const value = current.text.trim();
  const waiting = current.pending !== null;
  const unchanged = value === current.basis;
  useEffect(() => {
    if (waiting || unchanged) return;
    const timer = setTimeout(() => commit(value), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value, waiting, unchanged]);

  return (
    <InputGroup className={cn('w-full sm:w-64', className)}>
      <InputGroupAddon>
        <MagnifyingGlassIcon aria-hidden />
      </InputGroupAddon>
      <InputGroupInput
        type="search"
        aria-label={placeholder}
        placeholder={placeholder}
        maxLength={200}
        value={current.text}
        onChange={(e) => setState((s) => ({ ...s, text: e.target.value }))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !waiting && !unchanged) commitNow(value);
        }}
      />
    </InputGroup>
  );
}
